import { throttle, isEqual } from 'lodash';
import { PAGE_URL_SIGNUP } from '../constants';
import manager from './manager';
import sender from './sender';
import getHypotenuse from './tools/getHypotenuse';
import itWasBtn from './tools/itWasBtn';
import { DataCollector } from './data-collector/data-collector';
import { mouseSpeedCollector } from './collectors/mouse-speed';
import { scrollSpeedCollector } from './collectors/scroll-speed';
import { pressingDownDurationCollector } from './collectors/pressing-down-duration';
import { readTimeCollector } from './collectors/read-time';
import { scrollTypeCollector } from './collectors/scroll-type';
import { focusFrequencyCollector } from './collectors/focus-frequency';
import { mouseTouchFrequencyCollector } from './collectors/mouse-frequency';
import { capitalizeStringsCollector } from './collectors/capitalize-string';

export default function (w, n, d, s) {
  /*
   * следим за количеством ухода с вкладки
   */
  function countLeavePage() {
    let tracker = 0;
    let hidden = false;

    function trackerPlus() {
      if (d.hidden) {
        manager.addData('leave_page', tracker += 1, manager.types.replace);
      }
    }

    function trackerPlusSafari() {
      if (d.hidden && !hidden) {
        hidden = true;
        manager.addData('leave_page', tracker += 1, manager.types.replace);
      } else if (!d.hidden && hidden) {
        hidden = false;
      }
    }

    if (w.safari) {
      setInterval(trackerPlusSafari, 1000);
    } else {
      w.addEventListener('visibilitychange', trackerPlus);
    }
  }

  /*
   * отправляем показатели отклонения при нажатии на кнопку
   */
  function getCoordButtonClick() {
    function clickHandler({
      target, clientX, clientY,
    }) {
      if (!itWasBtn(target)) {
        return;
      }

      const {
        height, width, left, top,
      } = target.getBoundingClientRect();

      const deltaX = clientX - (left + (width / 2));
      const deltaY = clientY - (top + (height / 2));

      manager.addData('delta_btn', `${deltaX},${deltaY}`);
    }

    d.addEventListener('click', clickHandler);
  }

  /*
   * Следим за кликами на кнопки мыши: одинарный клик, даблклик, вызов контекстного меню
   */
  function countMouseBtnBehaviour() {
    let counterSingleClick = 0;
    let counterDblClick = 0;
    let counterCnxtClick = 0;
    let singleTimer;

    function counterSingleClickIncrement({ detail }) {
      if (detail === 2) {
        return;
      }

      singleTimer = setTimeout(() => {
        counterSingleClick += 1;
        manager.addData('sngl', counterSingleClick, manager.types.replace);
      }, 500);
    }

    function counterDblClickIncrement() {
      if (singleTimer) {
        clearTimeout(singleTimer);
      }
      counterDblClick += 1;

      manager.addData('dbl', counterDblClick, manager.types.replace);
    }

    function counterCnxtClickIncrement() {
      counterCnxtClick += 1;
      manager.addData('cnxt', counterCnxtClick, manager.types.replace);
    }

    w.addEventListener('dblclick', counterDblClickIncrement);
    w.addEventListener('click', counterSingleClickIncrement);
    w.addEventListener('contextmenu', counterCnxtClickIncrement);
  }

  /*
   * Проверяем посещение мышкой каждую четверть экрана на странице
   */
  function followMouseQuarter() {
    let acc = [0, 0, 0, 0];
    let centerX = w.innerWidth / 2;
    let centerY = w.innerHeight / 2;

    function handleMouseMove({ clientX, clientY }) {
      const currentAcc = [...acc];

      currentAcc[(clientX > centerX ? 1 : 0) + (clientY > centerY ? 2 : 0)] = 1;
      if (!isEqual(currentAcc, acc)) {
        acc = [...currentAcc];
        manager.addData('quarter', acc.join('|'), manager.types.replace);
      }

      if (acc.every(item => item)) {
        w.removeEventListener('mousemove', handleMouseMoveThrottle); // eslint-disable-line no-use-before-define, max-len
        w.removeEventListener('resize', handleScreenResizeThrottle); // eslint-disable-line no-use-before-define, max-len
      }
    }

    function handleScreenResize() {
      centerX = w.innerWidth / 2;
      centerY = w.innerHeight / 2;
    }

    const handleMouseMoveThrottle = throttle(handleMouseMove, 1000);
    const handleScreenResizeThrottle = throttle(handleScreenResize, 300);

    w.addEventListener('mousemove', handleMouseMoveThrottle);
    w.addEventListener('resize', handleScreenResizeThrottle);
  }

  /*
   * Замеряем время, проведенное на странице
   */
  function countTimeOnPage() {
    let currentPathname;
    let timer;

    function handlePageChange() {
      if (w.location.pathname === currentPathname) {
        return;
      }

      if (!timer) {
        timer = Date.now();
      } else {
        const currentTime = Date.now();

        manager.addData('pagetime', `${currentTime - timer}`);
        timer = currentTime;
      }
    }

    w.addEventListener('pagechange', handlePageChange);
  }

  /*
   * Считаем время задержки курсора над кнопкой перед кликом
   */
  function getTimeGapBeforeClick() {
    let timer;
    let smallTextRead = false;

    function handleMouseMove({ target }) {
      if (itWasBtn(target)) {
        if (!timer) {
          timer = Date.now();
        }
      } else if (timer) {
        timer = undefined;
      }

      if (
        target.nodeName === 'P' && target.classList.contains('small')
      ) {
        if (!smallTextRead) {
          smallTextRead = true;
          manager.addData('smalltextread', 1);
        }
      }
    }

    function handleClick() {
      if (timer) {
        manager.addData('btn_click_gap', `${Date.now() - timer}`);
      }
    }

    const handleMouseMoveThrottle = throttle(handleMouseMove, 300);

    w.addEventListener('mousemove', handleMouseMoveThrottle);
    w.addEventListener('click', handleClick);
  }

  /*
   * Считаем время взаимодействия с загрузки страницы до первого взаимодействия с инпутом
   */
  function getTimeFirstInputInteraction() {
    setTimeout(() => {
      const onPageTime = Date.now();
      let timeFristInteractionMeasured = false;

      const inputs = [...d.getElementsByTagName('input')];

      function handleFocus() {
        if (!timeFristInteractionMeasured) {
          manager.addData('timefirst', Date.now() - onPageTime);
          timeFristInteractionMeasured = true;
          inputs.forEach(item => item.removeEventListener('focus', handleFocus));
        }
      }
      inputs.forEach(item => item.addEventListener('focus', handleFocus));
    }, 0);
  }

  /*
   * Считаем время на заполнение полей формы
   */
  function getTimeInputInteraction() {
    const inputs = [...d.getElementsByTagName('input')];
    let currentName;
    let timer;

    const valueField = {
      email: '',
      fullname: '',
    };

    function handleFocus({ target: { name } }) {
      currentName = name;
      timer = Date.now();
    }

    function handleBlur({ target: { name, value } }) {
      if (name === currentName && valueField[name] !== value) {
        valueField[name] = value;
        manager.addData(currentName, Date.now() - timer);
      }
    }

    inputs.forEach((item) => {
      item.addEventListener('focus', handleFocus);
      item.addEventListener('blur', handleBlur);
    });
  }

  /*
   * Следим за курсором: средняя длина пути курсора, средняя скорость
   */
  function followMouseWay() {
    const coords = [];

    // Записываем координаты и время мышки
    function handleMove({ clientX, clientY, timeStamp }) {
      coords.push({ clientX, clientY, timeStamp });
    }

    setTimeout(() => {
      const dataSet = [];

      coords.forEach((item, index) => {
        if (index + 1 >= coords.length) {
          return false;
        }

        return dataSet.push(getHypotenuse(item, coords[index + 1]));
      });
    }, 5000);

    const handleMoveThrottle = throttle(handleMove, 150);

    window.addEventListener('mousemove', handleMoveThrottle);

    function handlePageChange() {
      if (w.location.pathname === PAGE_URL_SIGNUP) {
        return;
      }
      let dataFrame = [];
      let currentFrame = 0;

      // разбиваем данные на фреймы движения мышки
      coords.forEach((item, index) => {
        if (!coords[index + 1]) {
          return false;
        }

        if (coords[index + 1].timeStamp - item.timeStamp >= 500) {
          currentFrame += 1;
        }

        if (!dataFrame[currentFrame]) {
          dataFrame[currentFrame] = [];
          return false;
        }

        return dataFrame[currentFrame].push(item);
      });

      // готовим данные: считаем пройденный путь и время для каждой пары значений в каждом фрейме
      dataFrame = dataFrame.map((frame) => {
        const currentData = [];
        frame.forEach((item, index) => {
          if (!frame[index + 1]) {
            return null;
          }

          return currentData.push(getHypotenuse(item, frame[index + 1]));
        });

        return currentData;
      });

      // считаем путь и среднюю скорость для каждого фрейма
      const timeAndWay = dataFrame.map(tick => tick.reduce((acc, item) => ({
        time: acc.time + item.deltaTime,
        way: acc.way + item.hypotenuse,
      }), { time: 0, way: 0 }));

      manager.addData('mm', JSON.stringify(timeAndWay));
    }

    window.addEventListener('pagechange', handlePageChange);
  }

  /*
   * Cчитаем количество выделений объектов на странице
   */
  function countSelection() {
    let isSelected = '';
    let counter = 0;

    function intervalSelection() {
      const currentSelection = w.getSelection().toString();

      if (currentSelection.length !== 0 && !isSelected) {
        isSelected = true;
        counter += 1;

        manager.addData('sel', counter, manager.types.replace);
      }

      if (currentSelection.length === 0 && isSelected) {
        isSelected = false;
      }
    }

    setInterval(intervalSelection, 100);
  }

  /*
   * Время максимального разрыва между заполнением полей формы
   */
  function getTimerGapBetweenInput() {
    const inputs = [...d.getElementsByTagName('input')];
    let timing = 0;

    function handleChange({ target: { value } }) {
      if (timing === 0 && value.length > 0) {
        timing = Date.now();
      } else {
        manager.addData('doublegap', Date.now() - timing);
        inputs.forEach(item => item.removeEventListener('change', handleChange));
      }
    }
    inputs.forEach(item => item.addEventListener('change', handleChange));
  }

  /*
   * Получаем куку juicyLabs
   */
  function setJuicyCookie() {
    const interval = setInterval(() => {
      if (w.localStorage.jslbrc) {
        manager.addData('jslbrc', w.localStorage.jslbrc);
        clearInterval(interval);
      }
    }, 1000);
  }

  /*
   * Враппер для отслеживания текущей страницы и запуска функций для наблюдения
   */
  function followingUrl() {
    let currentPathname;

    function handlePageChange() {
      if (w.location.pathname === currentPathname) {
        return;
      }

      currentPathname = w.location.pathname;

      if (currentPathname === '/result') {
        // отслеживаем появление курсора в разных четвертях экрана
        followMouseQuarter();

        // следим за количеством выделений объектов на странице
        countSelection();
      }

      if (currentPathname === PAGE_URL_SIGNUP) {
        // считаем время до первого взаимодействия с формой
        getTimeFirstInputInteraction();

        // следим за движением курсора
        followMouseWay();

        // следим за количеством выделений обхектов на странице
        countSelection();

        // отслеживаем разные клики мышкой
        countMouseBtnBehaviour();

        // получаем и отправляем куку JuicyLab
        setJuicyCookie();

        setTimeout(() => {
          getTimeInputInteraction();
          getTimerGapBetweenInput();
        }, 0);
      }
    }
    w.addEventListener('pagechange', handlePageChange);
  }

  /*
   * Смотрим, сработал ли скрипт adverts.js
   */
  function checkAdblock() {
    manager.addData('adbl', w.adblockAbsent ? 0 : 1);
  }

  /*
   * Собираем основную информацию
   * Запускаем слежение за событиями
   * Инициируем асинхронных сбор данных
   */
  function gatheringMainData() {
    // считаем количество уходов со страницы
    countLeavePage();

    // отслеживаем отклонение от центра при клике по кнопке
    getCoordButtonClick();

    // отслеживаем время между фокусом кнопки и кликом по ней
    getTimeGapBeforeClick();

    // считаем время, проведенное на каждой странице
    countTimeOnPage();

    // запускаем сбор информации в зависимости от текущего урла
    followingUrl();

    // проверяем есть ли Adblock
    checkAdblock();
  }
  gatheringMainData();

  const dataCollector = new DataCollector({
    // distance|time|speed|frequency
    mouseSpeed: { collector: mouseSpeedCollector },
    // distance | speed
    scrollSpeed: { collector: scrollSpeedCollector },
    // duration | focus count | frequency
    focusFrequency: { collector: focusFrequencyCollector },
    // average duration
    pressdown_avg_duration: { collector: pressingDownDurationCollector },
    // max time | min time | average time
    readTime: { collector: readTimeCollector },
    // by scroll | by keydown | by touch
    scrollType: { collector: scrollTypeCollector },
    // frequency | count
    mouseFrequency: { collector: mouseTouchFrequencyCollector },
    // not capitalized count | capitalized count
    capitalizeStrings: { collector: capitalizeStringsCollector },
  }, (name, data) => manager.addData(name, data, manager.types.replace));

  dataCollector.start();


  /*
   * Расписание по отправке данных
   */
  const scheduler = {
    sendData(sessionId) {
      const data = manager.getData();
      sender(data, sessionId);
      manager.eraseAllData();
    },

    start() {
      // забираем куку сессии, установленную сервером

      const sendData = () => this.sendData(s);

      w.addEventListener('pagechange', sendData);

      setInterval(() => sendData(), 12000);
    },
  };

  scheduler.start();
}



// WEBPACK FOOTER //
// ./src/lib/index.js